import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import { IconButton } from '@src/common/components/IconButton';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { Dropzone } from '@src/common/Dropzone';
import type { AddPermitTypeFormInputs } from '@src/permitTypesManager/AddPermitTypeFormInputs';
import { CustomDocumentPdfFields } from '@src/permitTypesManager/permitTypeSections/CustomDocumentPdfFields';
import { Trash01 } from '@untitled-ui/icons-react';
import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const CustomDocumentPdfSection = () => {
  const { control } = useFormContext<AddPermitTypeFormInputs>();

  return (
    <Stack>
      <Controller
        name={CustomDocumentPdfFields.fieldName}
        control={control}
        render={({ field }) => {
          const currentFile = field.value as File | null | undefined;

          const onDrop = useCallback(
            (acceptedFiles: File[]) => {
              if (acceptedFiles.length > 0) {
                field.onChange(acceptedFiles[0]);
              }
            },
            [field],
          );

          const handleDeleteFile = useCallback(() => {
            field.onChange(null);
          }, [field]);

          return (
            <SingleDocumentDropzone
              onDrop={onDrop}
              currentFile={currentFile}
              onDelete={handleDeleteFile}
            />
          );
        }}
      />
    </Stack>
  );
};

const SingleDocumentDropzone = ({
  onDrop,
  onDelete,
  currentFile,
}: {
  onDrop: (acceptedFiles: File[]) => void;
  onDelete: () => void;
  currentFile: File | null | undefined;
}) => {
  const { t } = useTranslation();
  const isDisabled = !!currentFile;
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
    },
    disabled: isDisabled,
  });

  return (
    <Stack>
      {!isDisabled && (
        <Dropzone
          isSmall={!!currentFile}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          open={open}
          singleFile={true}
        />
      )}

      {currentFile && (
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography>
              {currentFile.name} -{' '}
              {roundToTwoDecimalsString(currentFile.size / 1024)} KB
            </Typography>
            <IconButton
              title={t('delete')}
              onClick={onDelete}
              aria-label={t('delete')}
            >
              <Trash01 />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
